import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { planDocumentAPI } from '../../services/api'
import CycleReportPdfButton from '../health-cycle/CycleReportPdfButton'
import WeeklyPlanDocument from './WeeklyPlanDocument'
import { contentLanguage } from '../../i18n/supported'

const uiLanguage = contentLanguage

// VH-229 refinement: the "Weekly plan" of one cycle week — shown in the reader's language
// (translated on demand from the stored base language), exportable as a PDF of the whole document.
// Writing, copying and adjusting a plan happen in the week panel (useWeekPlanning), never here.
export function WeeklyPlanView({ cycleId, planId, weekStart }) {
  const { t, i18n } = useTranslation()
  const ref = useRef(null)
  const language = uiLanguage(i18n.language)

  const query = useQuery({
    queryKey: ['planDocument', cycleId, planId, language],
    queryFn: () => planDocumentAPI.get(cycleId, planId, language).then((r) => r.data),
    enabled: !!cycleId && !!planId,
    retry: (count, err) => err?.response?.status !== 404 && count < 1,
  })

  const missing = query.error?.response?.status === 404
  const doc = query.data?.document

  return (
    <div className="space-y-3" data-testid="weekly-plan-view">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-900">{t('weeklyPlan.viewTitle')}</h4>
        <div className="flex flex-wrap items-center gap-2">
          {doc && (
            <CycleReportPdfButton targetRef={ref} fileName={`weekly-plan-${weekStart}.pdf`}
              stamp={t('weeklyPlan.pdfStamp', { week: weekStart })} />
          )}
        </div>
      </div>
      {query.isLoading && (
        <p className="text-sm text-gray-500" role="status">{t('weeklyPlan.loading')}</p>
      )}
      {missing && <p className="text-sm text-gray-600">{t('weeklyPlan.none')}</p>}
      {query.isError && !missing && <p className="text-sm text-red-600" role="alert">{t('weeklyPlan.errors.load')}</p>}
      {doc?.translation_failed && (
        <p className="text-sm text-amber-700" role="status">{t('weeklyPlan.translationFailed')}</p>
      )}
      {doc?.status === 'needs_review' && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="plan-needs-review">
          <p className="font-medium">{t('weeklyPlan.needsReview')}</p>
          <ul className="list-disc pl-5">
            {(doc.problems ?? []).map((p) => <li key={p} className="font-mono text-xs">{p}</li>)}
          </ul>
        </div>
      )}
      {doc?.source === 'fallback' && <p className="text-xs text-gray-500">{t('weeklyPlan.source.fallback')}</p>}
      {doc && (
        <div ref={ref} className="rounded-lg overflow-hidden border border-gray-200">
          <WeeklyPlanDocument document={doc} weekStart={query.data.week_start} />
        </div>
      )}
    </div>
  )
}

export default WeeklyPlanView
